import * as Actions from './notification-hub.actions';
import type { NotificationHubAction } from './notification-hub.actions';
import { NotificationFilter, NotificationPriority } from './notification-hub.types';
import type { Notification, NotificationHubState } from './notification-hub.types';

export const namespace = 'notificationHub';

const initialState: NotificationHubState = {
  isOpen: false,
  notifications: [],
  activeFilter: NotificationFilter.UNREAD,
  unreadCount: 0,
  criticalCount: 0,
  warningCount: 0,
  infoCount: 0,
  isLoading: false,
  hasError: false,
};

const computeUnreadCountByPriority = (notifications: Notification[], priority: string): number =>
  notifications.filter((n) => n.priority === priority && !n.isRead && !n.isDismissed).length;

const computeCriticalCount = (notifications: Notification[]): number =>
  computeUnreadCountByPriority(notifications, NotificationPriority.CRITICAL);

const computeWarningCount = (notifications: Notification[]): number =>
  computeUnreadCountByPriority(notifications, NotificationPriority.WARNING);

const computeInfoCount = (notifications: Notification[]): number => computeUnreadCountByPriority(notifications, NotificationPriority.INFO);

export const notificationHubReducer = (state: NotificationHubState = initialState, action: NotificationHubAction): NotificationHubState => {
  switch (action.type) {
    case Actions.TOGGLE_NOTIFICATION_HUB:
      return {
        ...state,
        isOpen: action.isOpen,
      };

    case Actions.SET_ACTIVE_FILTER:
      return {
        ...state,
        activeFilter: action.filter,
      };

    case Actions.SET_NOTIFICATIONS: {
      return {
        ...state,
        notifications: action.notifications,
        criticalCount: computeCriticalCount(action.notifications),
        warningCount: computeWarningCount(action.notifications),
        infoCount: computeInfoCount(action.notifications),
      };
    }

    case Actions.ADD_NOTIFICATION: {
      const updatedNotifications = [action.notification, ...state.notifications];
      const { priority } = action.notification;
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: state.unreadCount + 1,
        criticalCount: priority === NotificationPriority.CRITICAL ? state.criticalCount + 1 : state.criticalCount,
        warningCount: priority === NotificationPriority.WARNING ? state.warningCount + 1 : state.warningCount,
        infoCount: priority === NotificationPriority.INFO ? state.infoCount + 1 : state.infoCount,
      };
    }

    case Actions.SET_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.count,
      };

    case Actions.MARK_AS_READ: {
      const notification = state.notifications.find((n) => n.notificationID === action.notificationID);
      if (!notification || notification.isRead) {
        return state;
      }
      const { priority } = notification;
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.notificationID === action.notificationID ? { ...n, isRead: true, readUtc: action.timestampUtc } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
        criticalCount: priority === NotificationPriority.CRITICAL ? Math.max(0, state.criticalCount - 1) : state.criticalCount,
        warningCount: priority === NotificationPriority.WARNING ? Math.max(0, state.warningCount - 1) : state.warningCount,
        infoCount: priority === NotificationPriority.INFO ? Math.max(0, state.infoCount - 1) : state.infoCount,
      };
    }

    case Actions.MARK_ALL_AS_READ: {
      const updatedNotifications = state.notifications.map((n) => {
        if (n.isRead || n.isDismissed) {
          return n;
        }
        if (action.priorityFilter && n.priority !== action.priorityFilter) {
          return n;
        }
        return { ...n, isRead: true, readUtc: action.timestampUtc };
      });
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter((n) => !n.isRead && !n.isDismissed).length,
        criticalCount: computeCriticalCount(updatedNotifications),
        warningCount: computeWarningCount(updatedNotifications),
        infoCount: computeInfoCount(updatedNotifications),
      };
    }

    case Actions.DISMISS_NOTIFICATION: {
      const notification = state.notifications.find((n) => n.notificationID === action.notificationID);
      if (!notification) {
        return state;
      }
      const wasUnread = !notification.isRead;
      const { priority } = notification;
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.notificationID === action.notificationID ? { ...n, isDismissed: true, dismissedUtc: action.timestampUtc } : n,
        ),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        criticalCount: wasUnread && priority === NotificationPriority.CRITICAL ? Math.max(0, state.criticalCount - 1) : state.criticalCount,
        warningCount: wasUnread && priority === NotificationPriority.WARNING ? Math.max(0, state.warningCount - 1) : state.warningCount,
        infoCount: wasUnread && priority === NotificationPriority.INFO ? Math.max(0, state.infoCount - 1) : state.infoCount,
      };
    }

    case Actions.FETCH_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        isLoading: true,
        hasError: false,
      };

    case Actions.FETCH_NOTIFICATIONS_SUCCESS: {
      return {
        ...state,
        notifications: action.notifications,
        unreadCount: action.unreadCount,
        criticalCount: computeCriticalCount(action.notifications),
        warningCount: computeWarningCount(action.notifications),
        infoCount: computeInfoCount(action.notifications),
        isLoading: false,
        hasError: false,
      };
    }

    case Actions.FETCH_NOTIFICATIONS_FAILURE:
      return {
        ...state,
        isLoading: false,
        hasError: true,
      };

    default:
      return state;
  }
};
